import { useEffect, useRef } from 'react';
import { useAuth } from '@shared/components/AuthProvider/AuthProvider.context';

/**
 * Triggers a refetch after the auth provider identifies the user.
 *
 * SSR public-first renders the anonymous/public view. Once the client-side
 * GetMe query resolves and the auth provider dispatches `identifyUserAction`,
 * `hasInitializedOnce` flips to `true`. This hook detects that transition
 * and calls `refetch()` so the query re-runs with the auth token attached
 * (via the Apollo `authLink` which reads from `loginManager.getToken()`).
 *
 * The refetch only fires once — on the first `hasInitializedOnce` transition.
 * Subsequent re-renders are no-ops.
 *
 * When to use:
 * - Queries that run during SSR (`ssr: true`) and return different data based
 *   on auth state (e.g., EventWebsiteGuest's `activeSession`, admin-gated fields).
 * - NOT needed for queries that are client-only (`ssr: false`) — those already
 *   fire after hydration with the auth token automatically.
 *
 * @example
 * ```ts
 * const { data, loading } = useEventWebsiteGuestQuery({ ... });
 * useAuthUpgradeRefetch(result.refetch);
 * ```
 */
export const useAuthUpgradeRefetch = (refetch: (() => unknown) | undefined) => {
  const { hasInitializedOnce } = useAuth();
  const hasRefetchedRef = useRef(false);

  useEffect(() => {
    // Only refetch once, after auth has initialized for the first time.
    // During SSR and initial client render, hasInitializedOnce is false.
    // When GetMe resolves (success or failure), it becomes true.
    if (hasInitializedOnce && !hasRefetchedRef.current) {
      hasRefetchedRef.current = true;
      refetch?.();
    }
  }, [hasInitializedOnce, refetch]);
};
